import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { toast } from "sonner";
import { DEFAULT_SETTINGS } from "../data/sampleFeed";
import {
  outstandingActions,
  isUpdateDeferred,
  allActionsResolved,
  actionDoneToast,
  hasExpiredAction,
  isPastEvent,
  isConcludedInformationalOnly,
  resolveActionStatus,
  isFyiEvent,
  fyiViewState,
  isUpdateResolvedForClient,
  isHeldForOperatorReview,
  urgencySortKey,
  buildGoogleCalendarUrl,
  eventDateIso,
} from "../lib/frontrow";
import { onFeedInvalidated } from "../lib/frontrow_bus";
import { readCachedFeed, writeCachedFeed } from "../lib/feedCache";
import { useAuth } from "./AuthContext";
import { distinctChildColours } from "../lib/childColours";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "frontrow.state.v2";

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const FrontRowContext = createContext(null);
// Case 6 (2026-08-28) — exported so <UpdatedBadge/> can consume
// ``eventIdsSurfacedInRecentChanges`` without adding a new hook.
// The default provider (below) is unchanged.
export { FrontRowContext };

export const FrontRowProvider = ({ children }) => {
  const { familyId, clerkUser } = useAuth();
  const feedCacheIdentity = clerkUser?.id ? `${familyId}:${clerkUser.id}` : familyId;
  // Render the last server-confirmed family feed immediately, then refresh.
  const initialCachedFeed = useMemo(() => readCachedFeed(feedCacheIdentity), [feedCacheIdentity]);
  const [feed, setFeed] = useState(
    () => initialCachedFeed || { children: {}, events: [] }
  );
  const [feedReady, setFeedReady] = useState(() => Boolean(initialCachedFeed));
  const [feedRefreshing, setFeedRefreshing] = useState(false);
  const [feedRefreshFailed, setFeedRefreshFailed] = useState(false);

  // Promote a valid cached feed before the browser paints the loading state.
  useLayoutEffect(() => {
    if (initialCachedFeed) setFeedReady(true);
  }, [initialCachedFeed]);

  const persisted = loadPersisted() || {};
  // actionStatus: { [action_id]: "completed" | "snoozed" | "not_for_us" }
  const [actionStatus, setActionStatus] = useState(() => Object.fromEntries(
    Object.entries(persisted.actionStatus || {}).map(([id, value]) => [id,
      value === "snoozed" ? { status: "snoozed", deferred_at: new Date().toISOString() } : value
    ])
  ));
  const [onCalendar, setOnCalendar] = useState(persisted.onCalendar || {});
  const [viewedUpdates, setViewedUpdates] = useState(
    persisted.viewedUpdates || {}
  );
  const [settings, setSettings] = useState({
    ...DEFAULT_SETTINGS,
    ...(persisted.settings || {}),
  });
  const [calendarPromptEventId, setCalendarPromptEventId] = useState(null);
  const calendarInteractionRef = useRef(0);

  useEffect(() => {
    const snapshot = { actionStatus, onCalendar, viewedUpdates, settings };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      /* ignore */
    }
  }, [actionStatus, onCalendar, viewedUpdates, settings]);

  // Mobile/PWA launch guard: foreground + pageshow can fire together on
  // iOS. Coalesce those triggers into one feed request and suppress an
  // immediate repeat for five seconds. Explicit invalidations remain fresh.
  const feedRequestRef = useRef(null);
  const lastFeedRequestAtRef = useRef(0);
  const providerMountedRef = useRef(true);

  useEffect(() => () => {
    providerMountedRef.current = false;
  }, []);

  const refreshFeed = useCallback(({ maxAgeMs = 0 } = {}) => {
    const now = Date.now();
    if (feedRequestRef.current) return feedRequestRef.current;
    if (maxAgeMs > 0 && now - lastFeedRequestAtRef.current < maxAgeMs) {
      return Promise.resolve(null);
    }

    lastFeedRequestAtRef.current = now;
    setFeedRefreshing(true);
    setFeedRefreshFailed(false);
    const request = axios
      .get(`${API}/feed`)
      .then((res) => {
        if (providerMountedRef.current && res?.data) {
          setFeed(res.data);
          setFeedReady(true);
          writeCachedFeed(feedCacheIdentity, res.data);
        }
        return res?.data || null;
      })
      .catch(() => {
        if (providerMountedRef.current) setFeedRefreshFailed(true);
        return null;
      })
      .finally(() => {
        if (providerMountedRef.current) setFeedRefreshing(false);
        if (feedRequestRef.current === request) feedRequestRef.current = null;
      });
    feedRequestRef.current = request;
    return request;
  }, [feedCacheIdentity]);

  useEffect(() => {
    let cancelled = false;
    refreshFeed();
    // Subscribe to cross-tab / same-tab feed invalidation events.
    // Any publisher (Email Pipeline commit, /reprocess, future
    // background email poller) can call postFeedInvalidated() to make
    // the dashboard auto-refresh without a manual reload.
    const unsub = onFeedInvalidated(() => {
      if (!cancelled) refreshFeed();
    });

    // Auto-refetch on foreground (2026-02-17). Whenever the tab or
    // installed PWA becomes visible again we grab the latest feed so
    // the parent never sees stale data. `pageshow` covers the
    // bfcache restore case (iOS Safari when swiping back).
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (!cancelled) refreshFeed({ maxAgeMs: 5000 });
    };
    const onPageShow = () => {
      if (!cancelled) refreshFeed({ maxAgeMs: 5000 });
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("pageshow", onPageShow);
    }

    return () => {
      cancelled = true;
      unsub();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("pageshow", onPageShow);
      }
    };
  }, [refreshFeed]);

  const resolvePendingInput = useCallback(
    async (eventId, inputId, value) => {
      try {
        await axios.post(
          `${API}/events/pending-parent-inputs/${inputId}/resolve`,
          { resolution: value }
        );
        if (value === "not_for_us") {
          toast("Hidden from your list");
        } else if (value === "dismiss_update") {
          toast("Update dismissed");
        } else {
          toast.success("Assigned");
        }
      } catch {
        toast.error("Couldn't save that — please try again");
        return;
      }
      await refreshFeed();
    },
    [refreshFeed]
  );

  // Merge persisted action status back into the event objects for reads.
  // Server terminal statuses (completed, expired) always win over the
  // local optimistic override — see resolveActionStatus for the full
  // precedence rule. This ensures automatic completion evidence
  // arriving from the backend resolves an action even when the parent
  // had previously opened the link (local status: `in_progress`).
  const events = useMemo(
    () =>
      (feed.events || []).map((e) => ({
        ...e,
        actions: (e.actions || []).map((a) => ({
          ...a,
          status: resolveActionStatus(a.status, actionStatus[a.id], a.personal_deferred),
          deferred_at: a.personal_deferred === undefined ? (actionStatus[a.id]?.deferred_at || a.deferred_at || null) : a.deferred_at,
        })),
      })),
    [feed.events, actionStatus]
  );

  // Raw engine count retained for existing in-app consumers. The OS badge is
  // synced in FrontRow.jsx from visible parent-facing attention only, so a
  // hidden historical event can never leave a mystery badge on the PWA icon.
  const updatedEventCount = useMemo(
    () => events.reduce((n, e) => n + (e.is_updated ? 1 : 0), 0),
    [events]
  );

  const getEvent = useCallback(
    (id) => events.find((e) => e.event_id === id),
    [events]
  );

  // Auto-add zero-action events to calendar is DISABLED until real
  // external calendar delivery exists. Local `onCalendar[id]=true`
  // must never claim delivery it hasn't actually confirmed. The
  // setting is preserved so a future ticket that ships real delivery
  // can re-enable this path.
  useEffect(() => {
    // no-op — see comment above.
  }, [events, settings.autoAddNoAction]);

  // --- Action state transitions ---
  const setActionState = (actionId, newStatus) => {
    setActionStatus((s) => ({ ...s, [actionId]: newStatus }));
  };

  // Opening the external action link is separate from completion.
  // We do NOT show a toast, do NOT trigger the calendar prompt, and do
  // NOT mark the action complete. The action transitions to
  // `in_progress` so the "I've done this" confirmation appears; the
  // event remains in "Needs action" until the parent explicitly
  // confirms.
  const openAction = useCallback(
    (eventId, actionId) => {
      const event = getEvent(eventId);
      if (!event) return;
      const action = event.actions.find((a) => a.id === actionId);
      if (!action) return;
      const url = action.action_url;
      const isRealUrl =
        typeof url === "string" && url.length > 0 && url !== "#";
      if (isRealUrl) {
        try {
          window.open(url, "_blank", "noopener,noreferrer");
        } catch {
          /* pop-up blocked or non-browser env — still transition state */
        }
      }
      // Only flip to in_progress if not already resolved/in-progress.
      if (action.status === "outstanding") {
        setActionState(actionId, "in_progress");
      }
    },
    [getEvent]
  );

  const addToCalendar = useCallback(async (eventId, { silent } = {}) => {
    // Lightweight MVP delivery: open a prefilled Google Calendar
    // create URL in a new tab. Works on iOS Safari, Chrome, Android
    // and desktop with no Google / Outlook API integration. Parents
    // pick the destination calendar from their own account.
    //
    // Intent is still POSTed so the engine records `add_requested`
    // and future real-delivery workers can pick it up.
    const evt = getEvent(eventId);
    if (!evt || !eventDateIso(evt)) return false;
    if (evt) {
      const url = buildGoogleCalendarUrl(evt);
      if (url && typeof window !== "undefined") {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    }
    if (!silent) toast.success("Opening your calendar…");
    try {
      await axios.post(`${API}/events/${eventId}/calendar/add`);
      refreshFeed();
    } catch {
      // best-effort; feed refetch on next natural trigger will reconcile
    }
  }, [refreshFeed, getEvent]);

  const confirmCalendarAdded = useCallback(async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/calendar/confirm-added`);
      toast.success("Added to your calendar");
      await refreshFeed();
    } catch {
      toast.error("Couldn't save that — please try again");
    }
  }, [refreshFeed]);

  const declineCalendar = useCallback(async (eventId) => {
    // Persist parent intent so Engine merges never re-prompt for this
    // event, and other devices / sessions see the same state.
    try {
      await axios.post(`${API}/events/${eventId}/calendar/not_now`);
      refreshFeed();
    } catch {
      // silent — the client already dismissed the prompt
    }
  }, [refreshFeed]);

  const completeAction = useCallback(
    async (eventId, actionId) => {
      const calendarInteraction = calendarInteractionRef.current;
      const event = getEvent(eventId);
      if (!event) return;
      const action = event.actions.find((a) => a.id === actionId);
      if (!action) return;

      // Optimistic UI.
      const prevStatus = actionStatus[actionId] || action.status;
      setActionState(actionId, "completed");

      // Persist to the Engine. The backend runs the deterministic
      // recompute so readiness / calendar_status stay authoritative.
      try {
        const response = await axios.post(
          `${API}/events/${eventId}/actions/${actionId}/complete`,
          { evidence: "parent_marked_done" }
        );
        if (response.data?.ok !== true || response.data?.system_status !== "completed") {
          throw new Error("Completion was not confirmed by the server");
        }
      } catch (err) {
        // Rollback optimistic UI on failure.
        setActionState(actionId, prevStatus);
        toast.error("Could not save that — please try again");
        return;
      }
      // Confirm success only after the server has persisted completion.
      toast.success(actionDoneToast(action));
      // A request started before this save may still contain the duplicate.
      // Wait for it, then explicitly fetch the post-mutation state.
      if (feedRequestRef.current) await feedRequestRef.current;
      const refreshedFeed = await refreshFeed();
      const refreshedEvent = refreshedFeed?.events?.find((e) => e.event_id === eventId);

      // If completing this was the last outstanding action, consider calendar.
      // 'in_progress' actions count as still-outstanding — a parent may
      // have opened the link but not yet confirmed.
      // Deferred tasks are unfinished too; they cannot trigger a completion
      // calendar prompt merely because a sibling task was completed.
      const remaining = (refreshedEvent?.actions || event.actions).filter(
        (a) =>
          a.id !== actionId &&
          ["outstanding", "in_progress", "snoozed", "waiting"].includes(a.status)
      );
      // Only a real occurrence can be added, never a registration deadline.
      // A later Dismiss/Later/Not for us also cancels this pending offer.
      if (remaining.length === 0 && refreshedEvent && eventDateIso(refreshedEvent)
          && calendarInteraction === calendarInteractionRef.current) {
        // Issue 3c: never re-prompt if the parent already made a
        // decision for this event (add_requested OR not_now). The
        // feed carries the authoritative flag from the backend.
        const alreadyDecided =
          refreshedEvent.calendar_decision && refreshedEvent.calendar_decision !== "eligible";
        if (alreadyDecided) {
          // Nothing further to do — decision persisted, engine will
          // respect it on later merges.
        } else if (settings.askBeforeAdding) {
          setCalendarPromptEventId(eventId);
        } else if (settings.autoAddAfterAction) {
          addToCalendar(eventId, { silent: false });
        }
      }
    },
    [getEvent, actionStatus, refreshFeed, addToCalendar, settings.askBeforeAdding, settings.autoAddAfterAction]
  );

  const snoozeAction = useCallback(async (eventId, actionId) => {
    calendarInteractionRef.current += 1;
    setCalendarPromptEventId(null);
    try {
      await axios.post(`${API}/events/${eventId}/actions/${actionId}/defer`, { deferred: true });
      // A pre-save request must not overwrite the newly confirmed decision.
      if (feedRequestRef.current) await feedRequestRef.current;
      await refreshFeed();
      toast("Saved for later");
    } catch {
      toast.error("Couldn't save Later — please try again");
    }
  }, [refreshFeed]);

  // "Not done yet" — the parent has explicitly answered "not yet".
  //
  // Reverses ANY prior completion evidence for this action, whether
  // that came from the local optimistic ``in_progress`` engagement
  // state OR from a persisted server ``completed`` after a previous
  // "I've done this". Contract (2026-02-18):
  //   * Local optimistic override is cleared entirely so
  //     ``resolveActionStatus`` returns to the server value.
  //   * If the server still reports the action as ``completed``, the
  //     new ``/reopen`` endpoint flips it back to ``outstanding`` and
  //     the deterministic Engine recompute runs — so readiness,
  //     calendar_status and derived event chip status all snap back
  //     consistently. The backend also restores consent aliases
  //     absorbed by that completion; distinct siblings stay untouched.
  //   * The feed is refetched so the dashboard shows the restored
  //     Action Required card immediately — no manual refresh needed.
  //   * Silent: no toast / snackbar / modal — the answer is
  //     self-explanatory.
  const keepActionOutstanding = useCallback(
    async (eventId, actionId) => {
      const event = getEvent(eventId);
      if (!event) return;
      const action = event.actions.find((a) => a.id === actionId);
      if (!action) return;
      const wasCompleted = action.status === "completed";
      if (action.status === "snoozed" || action.personal_deferred) {
        try {
          await axios.post(`${API}/events/${eventId}/actions/${actionId}/defer`, { deferred: false });
        } catch {
          toast.error("Couldn't restore that — please try again");
          return;
        }
      }
      // Clear any local optimistic override so the derived status
      // returns to the server value. ``resolveActionStatus`` is
      // called via the ``events`` memo and will reflect this on the
      // next render.
      setActionStatus((s) => {
        if (!(actionId in s)) return s;
        const next = { ...s };
        delete next[actionId];
        return next;
      });
      if (wasCompleted) {
        try {
          const response = await axios.post(
            `${API}/events/${eventId}/actions/${actionId}/reopen`
          );
          // A consent undo can restore absorbed aliases too. Their old
          // optimistic completed flags must not override the restored feed.
          const restoredIds = response.data?.restored_action_ids || [];
          setActionStatus((s) => {
            const next = { ...s };
            for (const id of [actionId, response.data?.canonical_action_id, ...restoredIds]) {
              if (id) delete next[id];
            }
            return next;
          });
        } catch {
          toast.error("Couldn't restore that — please try again");
          return;
        }
      }
      // Never reuse a feed request that started before the reopen save.
      if (feedRequestRef.current) await feedRequestRef.current;
      await refreshFeed();
    },
    [getEvent, refreshFeed]
  );

  // Engage — the parent has interacted with the card in a way that
  // indicates awareness of the action (tapped the primary CTA OR
  // "View details"). Flips the local state to `in_progress` so the
  // reminder-resolution pill controls surface on the card. Idempotent.
  const engageAction = useCallback(
    (eventId, actionId) => {
      const event = getEvent(eventId);
      if (!event) return;
      const action = event.actions.find((a) => a.id === actionId);
      if (!action) return;
      if (action.status === "outstanding") {
        setActionState(actionId, "in_progress");
      }
    },
    [getEvent]
  );

  // Dismiss (2026-08-31 · closure release) — persisted server-side as
  // ``parent_status = "dismissed"`` on the action. The card is removed
  // ONLY after the backend confirms the write; on failure the card
  // stays and an honest error shows. Repeat dismissal is idempotent
  // (backend returns ok with already_dismissed). "I did this" remains
  // the separate completion control (completeAction → /complete).

  const showSavedUndo = useCallback((message, url, body, onRestored) => {
    let restoring = false;

    const undo = async () => {
      if (restoring) return;
      restoring = true;
      try {
        await axios.post(url, body);
      } catch (error) {
        restoring = false;
        if (error.response?.status === 409) {
          toast.error("This item has changed since that action.");
        } else {
          toast.error("Couldn't undo. Please try again.", {
            duration: 8000,
            action: { label: "Retry Undo", onClick: undo },
          });
        }
        return;
      }

      onRestored?.();
      try {
        await refreshFeed();
        toast.success("Restored to your list");
      } catch {
        toast.error("Undo saved. Refresh to see your list.");
      }
    };

    toast(message, {
      duration: 8000,
      action: { label: "Undo", onClick: undo },
    });
  }, [refreshFeed]);

  const dismissAction = useCallback(async (eventId, actionId) => {
    const previousLocalStatus = actionStatus[actionId];
    let response;
    try {
      response = await axios.post(`${API}/events/${eventId}/actions/${actionId}/dismiss`);
    } catch {
      toast.error("Couldn't remove that — please try again");
      return;
    }
    setActionState(actionId, "not_for_us");
    // Mirror the confirmed server state locally so the card leaves
    // immediately; the awaited refetch below reconciles with the
    // authoritative feed (has_missed_action recomputed server-side).
    setFeed((f) => ({
      ...f,
      events: (f.events || []).map((e) =>
        e.event_id === eventId
          ? {
              ...e,
              actions: (e.actions || []).map((a) =>
                a.id === actionId ? { ...a, parent_status: "dismissed" } : a
              ),
              has_missed_action:
                (isPastEvent(e) ||
                  (!e.event_start && !e.event_end && !e.event_date)) &&
                (e.actions || []).some(
                  (a) =>
                    a.id !== actionId &&
                    a.status === "expired" &&
                    String(a.parent_status || "").toLowerCase() !== "dismissed"
                ),
            }
          : e
      ),
    }));
    if (!response.data?.already_dismissed && response.data?.dismissed_at) {
      showSavedUndo(
        "Removed from your list",
        `${API}/events/${eventId}/actions/${actionId}/undo-dismiss`,
        { dismissed_at: response.data.dismissed_at },
        () => setActionState(actionId, previousLocalStatus),
      );
    } else {
      toast("Removed from your list");
    }
    refreshFeed();
  }, [refreshFeed, actionStatus, showSavedUndo]);

  // Dismiss acknowledges the current information. Not for us is an ongoing,
  // private opt-out. Neither changes the shared family's canonical event.
  const acknowledgeFyi = useCallback(async (eventId) => {
    let response;
    try {
      response = await axios.post(`${API}/events/${eventId}/fyi/acknowledge`);
    } catch {
      toast.error("Couldn't save that. Please try again.");
      return;
    }

    if (!response.data?.already_acknowledged &&
        response.data?.fyi_acknowledged_at) {
      showSavedUndo(
        "Acknowledged",
        `${API}/events/${eventId}/fyi/undo`,
        { acknowledged_at: response.data.fyi_acknowledged_at },
      );
    }
    await refreshFeed();
  }, [refreshFeed, showSavedUndo]);

  const restoreEvent = useCallback(async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/restore`);
      await refreshFeed();
      toast.success("Restored to your list");
    } catch {
      toast.error("Couldn't restore — please try again");
    }
  }, [refreshFeed]);

  const dismissEvent = useCallback(async (eventId) => {
    calendarInteractionRef.current += 1;
    setCalendarPromptEventId(null);
    setFeed((f) => ({
      ...f,
      events: (f.events || []).filter((e) => e.event_id !== eventId),
    }));
    try {
      await axios.post(`${API}/events/${eventId}/dismiss`);
      toast("Dismissed this information", {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: () => restoreEvent(eventId),
        },
      });
    } catch {
      // On failure re-fetch so the UI reflects true DB state.
      toast("Couldn't dismiss — refreshing");
      refreshFeed();
    }
  }, [refreshFeed, restoreEvent]);

  const notForUsEvent = useCallback(async (eventId) => {
    calendarInteractionRef.current += 1;
    setCalendarPromptEventId(null);
    try {
      await axios.post(`${API}/events/${eventId}/not-for-us`);
      setFeed(f => ({ ...f, events: (f.events || []).filter(e => e.event_id !== eventId) }));
      toast("Not for us saved", { duration: 8000,
        action: { label: "Undo", onClick: () => restoreEvent(eventId) } });
    } catch {
      toast.error("Couldn't save that. Please try again.");
      return false;
    }
    await refreshFeed();
    return true;
  }, [refreshFeed, restoreEvent]);

  const resolveCalendarPrompt = useCallback(
    (accept, { persistDecision = true } = {}) => {
      if (!calendarPromptEventId) return;
      const id = calendarPromptEventId;
      setCalendarPromptEventId(null);
      if (accept) {
        addToCalendar(id);
      } else if (persistDecision) {
        declineCalendar(id);
      }
    },
    [calendarPromptEventId, addToCalendar, declineCalendar]
  );

  const markUpdateViewed = useCallback((updateId) => {
    setViewedUpdates((s) => (s[updateId] ? s : { ...s, [updateId]: true }));
  }, []);

  const markEventUpdatesViewed = useCallback(
    (eventId) => {
      const event = getEvent(eventId);
      if (!event) return;
      setViewedUpdates((s) => {
        const next = { ...s };
        (event.updates || []).forEach((u) => {
          next[u.id] = true;
        });
        return next;
      });
    },
    [getEvent]
  );

  // Phase B.3 (2026-02-27) — record that the parent has opened this
  // event's Event Details page. Server writes `last_viewed_details_at`
  // and the next feed refresh recomputes `is_updated` so the UPDATED
  // badge clears on the dashboard until the next meaningful update.
  //
  // The badge only clears via THIS call — a plain dashboard refresh,
  // a login or app relaunch MUST NOT clear it. Errors are silent: the
  // badge simply stays and the next open will retry.
  const markDetailsViewed = useCallback(
    async (eventId) => {
      if (!eventId) return;
      try {
        await axios.post(`${API}/events/${eventId}/details-viewed`);
      } catch {
        return;
      }
      await refreshFeed();
    },
    [refreshFeed]
  );

  // --- Recent Changes: parent-explicit update resolution (2026-02).
  // Acknowledge and Dismiss are two DISTINCT terminal states on a
  // single update; neither mutates event.parent_state, actions,
  // pending_parent_inputs, or calendar decision. Server timestamps
  // are authoritative; local `viewedUpdates` remains as a
  // presentation/freshness helper so the card fades immediately
  // without waiting for the network round-trip.
  const resolveUpdateWithUndo = useCallback(
    async (eventId, updateId, operation) => {
      let response;
      try {
        response = await axios.post(
          `${API}/events/${eventId}/updates/${updateId}/${operation}`
        );
      } catch {
        toast.error("Couldn't save that. Please try again.");
        return;
      }

      setViewedUpdates((state) => ({ ...state, [updateId]: true }));
      const resolution = response.data?.resolution;
      const resolvedAt = resolution === "acknowledged"
        ? response.data?.acknowledged_at
        : response.data?.dismissed_at;

      if (!response.data?.already_resolved && resolvedAt &&
          (resolution === "acknowledged" || resolution === "dismissed")) {
        showSavedUndo(
          resolution === "acknowledged" ? "Update acknowledged" : "Update dismissed",
          `${API}/events/${eventId}/updates/${updateId}/undo`,
          { resolution, resolved_at: resolvedAt },
          () => setViewedUpdates((state) => {
            const next = { ...state };
            delete next[updateId];
            return next;
          }),
        );
      }
      await refreshFeed();
    },
    [refreshFeed, showSavedUndo]
  );

  const acknowledgeUpdate = useCallback(
    (eventId, updateId) => resolveUpdateWithUndo(eventId, updateId, "acknowledge"),
    [resolveUpdateWithUndo]
  );

  const dismissUpdate = useCallback(
    (eventId, updateId) => resolveUpdateWithUndo(eventId, updateId, "dismiss"),
    [resolveUpdateWithUndo]
  );

  const updateSetting = useCallback((key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
  }, []);

  // --- Derived home-screen views ---
  // Coming up = upcoming Front Row events (not external calendar state).
  // An event belongs here when:
  //   - it has no outstanding actions
  //   - it has no unresolved pending parent inputs
  //   - AND either event_start is in the future, OR event_date is on/after
  //     today, OR both are unknown. The no-date fallback is INTENTIONAL:
  //     events surface until the Event Engine expires them or the
  //     parent dismisses. It is NOT an accidental catch-all.
  // onCalendar[event_id] is purely a per-event badge/state signal —
  // it is deliberately NOT a section filter here.
  const information = useMemo(() => {
    return events
      .filter((event) =>
        event &&
        !isHeldForOperatorReview(event) &&
        (event.dashboard_visible === true || !isPastEvent(event, Date.now())) &&
        isFyiEvent(event) &&
        fyiViewState(event) !== "resolved"
      )
      .sort((a, b) => urgencySortKey(a) - urgencySortKey(b));
  }, [events]);

  const comingUp = useMemo(() => {
    const now = Date.now();
    const filtered = events.filter((e) => {
      // Held for operator review — never shown to parents (2026-08-31).
      if (isHeldForOperatorReview(e)) return false;
      // Past events leave the active dashboard entirely — even if the
      // parent hasn't tapped I've done this / Not for us. The
      // canonical record is retained server-side for History.
      if (e.dashboard_visible !== true && isPastEvent(e, now)) return false;
      // FYI records belong exclusively to the dedicated Information
      // lane. They must never inflate or mix into Coming Up.
      if (isFyiEvent(e)) return false;
      if (outstandingActions(e).length > 0) return false;
      if ((e.pending_inputs || []).length > 0) return false;
      // If we reach this branch the event is not past AND has no
      // active outstanding action or pending input. Show if:
      //  * it carries a future event_start / event_end / event_date, OR
      //  * it carries an UNRESOLVED expired action — the deadline has
      //    passed but the parent still needs to see the event so they
      //    can dismiss it or catch up. Per the P0 invariant "the
      //    event itself is upcoming so the event remains visible;
      //    only the expired action drops the item out of Action
      //    Required into Coming Up with a 'Deadline passed' chip.", OR
      //  * undated + unacknowledged meaningful update (Silly Socks
      //    style — visible until the parent acknowledges).
      if (e.event_start || e.event_end || e.event_date) return true;
      if (hasExpiredAction(e)) return true;
      // The active feed already accepted this information. Lack of a
      // calendar anchor must not erase it at this second handoff.
      if (e.dashboard_visible === true) return true;
      const hasUnackMeaningful = (e.updates || []).some(
        (u) =>
          u.classification === "meaningful" &&
          !u.acknowledged_at &&
          !u.dismissed_at &&
          !viewedUpdates[u.id]
      );
      return hasUnackMeaningful;
    });
    // Sort by soonest date. `event_start` (with time) and `event_date`
    // (date-only, local midnight) are compared on a common timeline so
    // a date-only Book Week on 24 AUG naturally follows a timed event
    // on 20 AUG. Events with no date sink to the bottom.
    const sortKey = (e) => {
      if (e.event_start) return new Date(e.event_start).getTime();
      if (e.event_date) {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(e.event_date);
        if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
      }
      return Number.POSITIVE_INFINITY;
    };
    return [...filtered].sort((a, b) => sortKey(a) - sortKey(b));
  }, [events, viewedUpdates]);

  // 2026-02 · ONE-PUBLISH · "Waiting on details" removed.
  //
  // The former sub-batch 5B `waitingOnDetails` strand rendered a
  // hourglass card for active/ready/dateless events. Parent
  // feedback: this state is not useful — a card with no date and
  // no action doesn't help decide anything. New rule:
  //   * dated no-action items surface in Coming Up naturally
  //     (event_date/event_start already present);
  //   * dateless no-action items are hidden from the main
  //     dashboard and remain accessible via the event detail /
  //     history route only.
  // See `lib/dashboardClassification.js` and the
  // `dashboardClassification.test.js` for the deterministic
  // parent-first mapping.

  // Phase D · 2026-08-27 · Release-blocking Missed / Needs review.
  //
  // Backend `has_missed_action` marks any event with an expired
  // uncompleted, non-dismissed action. This lane surfaces those so
  // Specialist Interviews (expired BOOK past its deadline) and any
  // future missed obligation NEVER silently disappear. Completing
  // or dismissing the action clears the flag (backend sets
  // system_status away from "expired" or parent_status to
  // "dismissed" / "acknowledged").
  //
  // Excludes acknowledged-and-dismissed items — those stay off the
  // dashboard by design.
  const missedActions = useMemo(() => {
    return events.filter(
      (e) =>
        e && e.has_missed_action === true && !isHeldForOperatorReview(e)
    );
  }, [events]);

  const needsAction = useMemo(
    () => {
      const items = events.filter((e) => {
        // Past events leave Action Required even when a BOOK / RSVP
        // action is still outstanding.
        if (isPastEvent(e)) return false;
        // FYI is NEVER Action Required — Acknowledge is not a
        // real-world obligation.
        if (isFyiEvent(e)) return false;
        // 2026-08-31 · held records are excluded above; Action
        // Required carries real-world obligations only.
        return (e.pending_inputs || []).length > 0 || outstandingActions(e).length > 0;
      });
      // Urgency-sort (2026-02-15): earliest relevant upcoming moment
      // — today before tomorrow before later, undated last.
      items.sort((a, b) => {
        const ka = urgencySortKey(a);
        const kb = urgencySortKey(b);
        return ka < kb ? -1 : ka > kb ? 1 : 0;
      });
      return items;
    },
    [events]
  );

  // 2026-08-31 · closure release: the parent-facing "Needs a quick
  // look" lane has been REMOVED. Ambiguous records (unresolved
  // pending parent inputs) are preserved server-side in held state
  // for operator review and are excluded from every parent lane and
  // count via isHeldForOperatorReview above.

  // Recent changes: any update explicitly marked notify_parent and not yet
  // viewed. CHANGE / LOGISTICS / PREPARE updates may legitimately carry the
  // "informational" classification; excluding them made UPDATED silently
  // fail. Standalone FYI records remain exclusively in Information.
  //
  // Multiple updates emitted by the SAME source email for the SAME event
  // (i.e. same event_id + same received_at) are grouped into ONE card so
  // the parent sees "Tickets confirmed · Friday 21 Aug · 6:30 pm · Audrey
  // Keown Theatre" rather than four cards for one real-world change.
  // The underlying `updates` array carries every individual record so
  // history / mark-viewed / Event Detail still show them all.
  const recentChanges = useMemo(() => {
    const groups = new Map(); // key = event_id + "|" + received_at
    const nowMs = Date.now();
    events.forEach((e) => {
      // Past event: unless the meaningful update is genuinely
      // post-event (received_at strictly after event_end / event_date),
      // the parent no longer needs to see it. Simplest safe rule:
      // drop past events from Recent Changes entirely. Truly-new
      // post-event updates come in on a live re-ingest and re-surface
      // if the event is later reactivated.
      if (isPastEvent(e, nowMs)) return;
      // Held for operator review — never surfaced in Recent changes
      // (2026-08-31 closure release).
      if (isHeldForOperatorReview(e)) return;
      if (isFyiEvent(e)) return;
      // Initial content is not a change to an existing dashboard item.
      // The engine sets is_updated only after a later canonical change.
      if (e.is_updated !== true) return;
      // Keep obligations in Needs Action rather than duplicating their card.
      if (needsAction.some((item) => item.event_id === e.event_id)) return;
      // 2026-08-30 — dashboard bottom/recent/past area rule:
      // concluded informational / event-only cards must disappear
      // once the Sydney event-end passes. Recent Changes is part of
      // that surface, so a would-be "meaningful" update on an event
      // that no longer belongs on the dashboard is also suppressed.
      // Completed evidence (BOOK/RSVP/CONSENT/PAY/REGISTER/ORDER/
      // REPLY with parent_status=done) keeps the event live here
      // because it fails the concluded-informational predicate.
      if (isConcludedInformationalOnly(e, nowMs)) return;
      (e.updates || []).forEach((u) => {
        if (!u.notify_parent) return;
        if (isUpdateDeferred(e, u)) return;
        // Server-authoritative resolution first (either terminal
        // state hides the update immediately). Then the local
        // viewedUpdates cache handles the optimistic hide before the
        // POST completes.
        if (isUpdateResolvedForClient(u, viewedUpdates)) return;
        const key = `${e.event_id}|${u.received_at || ""}`;
        if (!groups.has(key)) {
          groups.set(key, {
            id: `grp_${e.event_id}_${u.received_at || ""}`,
            eventId: e.event_id,
            eventTitle: e.display_headline || e.event_name,
            children: e.children,
            received_at: u.received_at,
            classification: u.classification,
            notify_parent: u.notify_parent,
            updates: [],
          });
        }
        groups.get(key).updates.push(u);
      });
    });
    return Array.from(groups.values()).sort((a, b) =>
      (b.received_at || "").localeCompare(a.received_at || "")
    );
  }, [events, viewedUpdates, needsAction]);

  // Case 6 (2026-08-28) — event IDs whose meaningful unviewed
  // update is ALREADY surfaced in Recent Changes above.  The
  // per-card <UpdatedBadge/> reads this set and hides itself for
  // those events so the same change is communicated once (in
  // Recent Changes) rather than twice (Recent Changes + inline
  // card badge).  Events NOT in this set (e.g. server-flagged
  // is_updated events whose updates do not meet the meaningful+
  // notify_parent gate for Recent Changes) still show the inline
  // badge because Recent Changes will not communicate the change
  // for them.
  const eventIdsSurfacedInRecentChanges = useMemo(() => {
    const s = new Set();
    for (const g of recentChanges) {
      if (g && g.eventId) s.add(g.eventId);
    }
    return s;
  }, [recentChanges]);

  const colouredChildren = useMemo(() => distinctChildColours(feed.children || {}), [feed.children]);

  const value = {
    feed,
    canChangeActions: feed?.can_change_actions !== false,
    feedReady,
    feedRefreshing,
    feedRefreshFailed,
    events,
    children: colouredChildren,
    needsAction,
    comingUp,
    information,
    missedActions,
    updatedEventCount,
    ofInterest: [], // 2026-02-21 deprecated — Of Interest removed as a product category. Retained as empty array for backward compat with consumers.
    recentChanges,
    eventIdsSurfacedInRecentChanges,
    onCalendar,
    actionStatus,
    viewedUpdates,
    settings,
    calendarPromptEventId,
    getEvent,
    openAction,
    completeAction,
    snoozeAction,
    dismissAction,
    keepActionOutstanding,
    engageAction,
    dismissEvent,
    notForUsEvent,
    restoreEvent,
    acknowledgeFyi,
    addToCalendar,
    confirmCalendarAdded,
    declineCalendar,
    markUpdateViewed,
    markEventUpdatesViewed,
    markDetailsViewed,
    acknowledgeUpdate,
    dismissUpdate,
    resolveCalendarPrompt,
    resolvePendingInput,
    refreshFeed,
    updateSetting,
  };

  return (
    <FrontRowContext.Provider value={value}>
      {children}
    </FrontRowContext.Provider>
  );
};

export const useFrontRow = () => {
  const ctx = useContext(FrontRowContext);
  if (!ctx) throw new Error("useFrontRow must be used within FrontRowProvider");
  return ctx;
};

